import { useRef, useState, type PointerEvent as ReactPointerEvent } from 'react'

// Drag-to-resize bounds for a right-docked drawer.
const DRAWER_MIN_WIDTH = 380
const DRAWER_MAX_WIDTH = 900
const DRAWER_DEFAULT_WIDTH = 480

// Width state + drag handler for a right-docked, drag-to-resize drawer, persisted
// per `storageKey`. Extracted from the Care Queue drawer so every drawer shares one
// implementation; the caller owns the separator element and passes `startResize` to
// its `onPointerDown`. A caller can widen its own drawer via `defaultWidth`/`maxWidth`
// without affecting the others.
export function useResizableDrawer(
  storageKey: string,
  options?: { defaultWidth?: number; maxWidth?: number },
) {
  const defaultWidth = options?.defaultWidth ?? DRAWER_DEFAULT_WIDTH
  const maxWidth = options?.maxWidth ?? DRAWER_MAX_WIDTH

  const [width, setWidth] = useState(() => {
    const saved = Number(localStorage.getItem(storageKey))
    return saved >= DRAWER_MIN_WIDTH && saved <= maxWidth ? saved : defaultWidth
  })
  const widthRef = useRef(width)
  const [resizing, setResizing] = useState(false)

  const startResize = (e: ReactPointerEvent<HTMLDivElement>) => {
    e.preventDefault()
    setResizing(true)
    // Panel is docked right, so its width is the gap between the pointer and the
    // right edge of the viewport. Cap so it can't swallow the whole screen.
    const max = Math.min(maxWidth, window.innerWidth - 100)
    const onMove = (ev: PointerEvent) => {
      const next = Math.min(max, Math.max(DRAWER_MIN_WIDTH, window.innerWidth - ev.clientX))
      widthRef.current = next
      setWidth(next)
    }
    const onUp = () => {
      document.removeEventListener('pointermove', onMove)
      document.removeEventListener('pointerup', onUp)
      document.body.style.userSelect = ''
      setResizing(false)
      localStorage.setItem(storageKey, String(widthRef.current))
    }
    document.body.style.userSelect = 'none'
    document.addEventListener('pointermove', onMove)
    document.addEventListener('pointerup', onUp)
  }

  return { width, resizing, startResize }
}
